/**
 * O painel Arquivos da mesa (system/roomFiles; docs/spec/desk-cloud.md): os arquivos ligados a ela, por
 * tipo. Os mestres veem os dos mestres; cada jogador, só os dele (sem spoiler: o mapa preparado não
 * aparece aqui antes de o mestre mostrar).
 *
 * Tokens se arrastam para a cena; mapas abrem como cena (o selo "Na cena" marca o fundo aberto);
 * trilhas tocam para todos; documentos vão para o chat. Tudo se arrasta para a cena, o chat e o player
 * de música, e cada arquivo tem o menu do botão direito. No rodapé, "Todo o Desk Cloud" liga arquivos
 * que já estão lá, e "Enviar" manda novos, já ligados a esta mesa.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api.js';
import { formatBytes } from '../bytes.js';
import { sendToCloud } from '../files.js';
import { translate, useLanguage, useT } from '../i18n/index.js';
import { serverUrl } from '../serverUrl.js';
import { FILE_KINDS, type DeskFile, type FileKind } from '../shared.js';
import { useLoggedNav } from '../states/nav.js';
import { describeError, useApp } from '../store.js';
import { ContextMenu, type MenuAnchor, type MenuEntry } from '../ui/ContextMenu.js';
import { Button, Spinner } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { toast } from '../ui/Toaster.js';
import { useLongPress } from '../ui/touch.js';
import { uploadRefusal } from '../plans/planModel.js';
import { CloudFilePicker } from './CloudFilePicker.js';
import { panelMenu } from './cloudMenus.js';
import { KIND_COLORS, durationText, splitName, type UseAction } from './cloudModel.js';
import { setFileDrag } from './fileDrag.js';
import { applyInRoom, nameOf, useSceneBackgrounds } from './inRoom.js';
import './cloud.css';

const say = (key: string, params?: Record<string, string | number>) => translate(useLanguage.getState().language, key, params);

export function RoomFilesModule() {
  const t = useT();
  const token = useApp((s) => s.token);
  const room = useApp((s) => s.room);
  const me = useApp((s) => s.user?.id ?? null);
  const roomId = room?.id ?? null;
  const version = useApp((s) => (roomId ? (s.roomFilesVersion[roomId] ?? 0) : 0));
  const filesVersion = useApp((s) => s.filesVersion);
  const audio = useApp((s) => s.audio);
  const backgrounds = useSceneBackgrounds((s) => s.byScene);
  const [files, setFiles] = useState<DeskFile[] | null>(null);
  const [gm, setGm] = useState(false);
  const [kind, setKind] = useState<FileKind | null>(null);
  const [query, setQuery] = useState('');
  const [menu, setMenu] = useState<{ anchor: MenuAnchor; entries: MenuEntry[]; label: string } | null>(null);
  const [picking, setPicking] = useState(false);
  const [sending, setSending] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!token || !roomId) return;
    let alive = true;
    api.roomFiles(token, roomId).then(
      (result) => {
        if (!alive) return;
        setFiles(result.files);
        setGm(result.gm);
      },
      (error: unknown) => alive && toast(describeError(error), 'error'),
    );
    return () => {
      alive = false;
    };
  }, [token, roomId, version, filesVersion]);

  const onScene = useMemo(() => new Set(Object.values(backgrounds).filter((id): id is string => !!id)), [backgrounds]);
  const linked = useMemo(() => new Set((files ?? []).map((file) => file.id)), [files]);
  const q = query.trim().toLocaleLowerCase();
  const shown = (files ?? []).filter((file) => (!kind || file.kind === kind) && (!q || file.name.toLocaleLowerCase().includes(q)));
  const counts = Object.fromEntries(FILE_KINDS.map((k) => [k, (files ?? []).filter((file) => file.kind === k).length])) as Record<FileKind, number>;
  const playingUrl = audio?.isPlaying ? audio.current?.url : null;

  if (!roomId) return null;
  const use = (file: DeskFile, action: UseAction) => void applyInRoom(file, action, roomId);
  const openMenu = (file: DeskFile, anchor: MenuAnchor) =>
    setMenu({
      anchor,
      label: file.name,
      entries: panelMenu(
        { t },
        file,
        { gm, mine: file.ownerId === me, roomId },
        {
          use,
          open: (f) => window.open(serverUrl(f.url), '_blank', 'noopener'),
          download: (f) => window.open(`${serverUrl(f.url)}&dl=1`, '_self'),
          reveal: (f) => useLoggedNav.getState().go({ kind: 'cloud', at: { view: 'room', roomId }, focus: f.id }),
          unlink: (f) =>
            void (async () => {
              if (!token) return;
              try {
                await api.setFileRooms(token, [f.id], [], [roomId]);
                toast(t('cloud.panel.unlinked', { name: f.name }), 'info');
              } catch (error) {
                toast(describeError(error), 'error');
              }
            })(),
        },
      ),
    });

  const send = async (list: FileList) => {
    setSending(true);
    try {
      let used = useApp.getState().fileUsage?.used ?? null;
      for (const file of list) {
        const refusal = uploadRefusal(useApp.getState(), file.size, say, used);
        if (refusal) throw new Error(refusal);
        await sendToCloud(file, { roomId });
        if (used !== null) used += file.size;
      }
    } catch (error) {
      toast(describeError(error), 'error');
    } finally {
      setSending(false);
    }
  };

  const byKind = (k: FileKind) => shown.filter((file) => file.kind === k);
  const section = (k: FileKind, body: React.ReactNode, hint?: string) =>
    byKind(k).length ? (
      <section key={k} className="rd-roomFiles__section" aria-label={t(`cloud.kinds.${k}`)}>
        <div className="rd-roomFiles__sectionHead">
          <h3>{t(`cloud.kinds.${k}`)}</h3>
          <span>{hint ?? byKind(k).length}</span>
        </div>
        {body}
      </section>
    ) : null;

  return (
    <div className="rd-roomFiles">
      <div className="rd-roomFiles__tools">
        <label className="rd-searchBox rd-roomFiles__search">
          <Icon name="search" size={15} />
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('cloud.panel.search')} aria-label={t('cloud.panel.search')} />
        </label>
        <div className="rd-chips rd-roomFiles__chips" role="group" aria-label={t('cloud.filter')}>
          <button type="button" className={`rd-chip${kind === null ? ' is-on' : ''}`} aria-pressed={kind === null} onClick={() => setKind(null)}>
            {t('cloud.kinds.all')}
          </button>
          {FILE_KINDS.filter((k) => counts[k] > 0 || kind === k).map((k) => (
            <button key={k} type="button" className={`rd-chip${kind === k ? ' is-on' : ''}`} aria-pressed={kind === k} onClick={() => setKind(kind === k ? null : k)}>
              {t(`cloud.kinds.${k}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="rd-roomFiles__body">
        {files === null ? (
          <div className="rd-module__placeholder">
            <Spinner size={22} />
          </div>
        ) : !files.length ? (
          <p className="rd-roomFiles__empty">{gm ? t('cloud.panel.empty') : t('cloud.panel.emptyPlayer')}</p>
        ) : (
          <>
            {section(
              'token',
              <div className="rd-roomFiles__tiles is-tokens">
                {byKind('token').map((file) => (
                  <Tile key={file.id} file={file} onMenu={openMenu} onActivate={gm ? () => use(file, 'token') : undefined} round />
                ))}
              </div>,
              t('cloud.panel.dragHint'),
            )}
            {section(
              'map',
              <div className="rd-roomFiles__tiles">
                {byKind('map').map((file) => (
                  <Tile key={file.id} file={file} onMenu={openMenu} onActivate={gm ? () => use(file, 'scene') : undefined} badge={onScene.has(file.id) ? t('cloud.panel.onScene') : gm ? t('cloud.use.scene') : undefined} badgeOn={onScene.has(file.id)} />
                ))}
              </div>,
            )}
            {section(
              'audio',
              <ul className="rd-roomFiles__rows">
                {byKind('audio').map((file) => {
                  const playing = playingUrl === `file:${file.id}`;
                  return (
                    <Row
                      key={file.id}
                      file={file}
                      onMenu={openMenu}
                      sub={playing ? t('cloud.panel.playingAll') : file.durationMs !== null ? durationText(file.durationMs) : formatBytes(file.size)}
                      action={
                        gm ? (
                          <button
                            type="button"
                            className={`rd-roomFiles__play${playing ? ' is-playing' : ''}`}
                            aria-label={playing ? t('cloud.panel.pauseAll') : t('cloud.panel.playAll', { name: nameOf(file.name) })}
                            onClick={() => (playing ? useApp.getState().controlAudio('pause') : use(file, 'play'))}
                          >
                            <Icon name={playing ? 'pause' : 'play'} size={14} filled />
                          </button>
                        ) : null
                      }
                    />
                  );
                })}
              </ul>,
            )}
            {(['doc', 'other'] as const).map((k) =>
              section(
                k,
                <ul className="rd-roomFiles__rows">
                  {byKind(k).map((file) => (
                    <Row
                      key={file.id}
                      file={file}
                      onMenu={openMenu}
                      sub={`${splitName(file.name).ext.toUpperCase() || t(`cloud.kind.${file.kind}`)} · ${formatBytes(file.size)}`}
                      action={
                        <button type="button" className="rd-iconBtn" aria-label={t('cloud.panel.toChat', { name: nameOf(file.name) })} title={t('cloud.use.chat')} onClick={() => use(file, 'chat')}>
                          <Icon name="send" size={15} />
                        </button>
                      }
                    />
                  ))}
                </ul>,
              ),
            )}
          </>
        )}
      </div>

      <div className="rd-roomFiles__foot">
        <div className="rd-roomFiles__footButtons">
          {gm ? (
            <Button variant="secondary" icon="cloud" onClick={() => setPicking(true)}>
              {t('cloud.panel.whole')}
            </Button>
          ) : null}
          <Button variant="action" icon="upload" busy={sending} onClick={() => inputRef.current?.click()}>
            {t('cloud.panel.send')}
          </Button>
        </div>
        <span className="rd-roomFiles__hint">{t('cloud.panel.sendHint')}</span>
        <input
          ref={inputRef}
          type="file"
          multiple
          hidden
          onChange={(e) => {
            if (e.target.files?.length) void send(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      {menu ? <ContextMenu anchor={menu.anchor} entries={menu.entries} label={menu.label} onClose={() => setMenu(null)} /> : null}
      {picking ? <PickFromCloud roomId={roomId} linked={linked} onClose={() => setPicking(false)} /> : null}
    </div>
  );
}

/** Um mapa ou um token: a imagem, que se arrasta para a cena; clicar faz a ação principal (do mestre). */
function Tile({ file, onMenu, onActivate, badge, badgeOn, round }: { file: DeskFile; onMenu: (file: DeskFile, at: MenuAnchor) => void; onActivate?: () => void; badge?: string; badgeOn?: boolean; round?: boolean }) {
  const press = useLongPress();
  const src = file.thumbUrl ?? (file.mime.startsWith('image/') ? file.url : null);
  return (
    <button
      type="button"
      className={`rd-roomFiles__tile${round ? ' is-round' : ''}`}
      title={file.name}
      draggable
      onDragStart={(e) => setFileDrag(e.dataTransfer, file)}
      onClick={onActivate}
      {...press.bind((at) => onMenu(file, at))}
    >
      <span className="rd-roomFiles__img">{src ? <img src={serverUrl(src)} alt="" loading="lazy" draggable={false} /> : <Icon name="file" size={20} />}</span>
      <span className="rd-roomFiles__name">{nameOf(file.name)}</span>
      {badge ? <span className={`rd-roomFiles__badge${badgeOn ? ' is-on' : ''}`}>{badge}</span> : null}
    </button>
  );
}

/** Uma trilha, um documento ou outro: o nome, a linha de baixo e a ação; arrasta para o chat e a música. */
function Row({ file, sub, action, onMenu }: { file: DeskFile; sub: string; action: React.ReactNode; onMenu: (file: DeskFile, at: MenuAnchor) => void }) {
  const press = useLongPress();
  return (
    <li className="rd-roomFiles__row" draggable onDragStart={(e) => setFileDrag(e.dataTransfer, file)} {...press.bind((at) => onMenu(file, at))}>
      <span className="rd-cloudDot" style={{ background: KIND_COLORS[file.kind] }} aria-hidden="true" />
      <span className="rd-roomFiles__rowText">
        <span className="rd-roomFiles__name">{nameOf(file.name)}</span>
        <span className="rd-roomFiles__sub">{sub}</span>
      </span>
      {action}
    </li>
  );
}

/** "Todo o Desk Cloud": os meus arquivos que ainda não estão nesta mesa, para ligar de uma vez. */
function PickFromCloud({ roomId, linked, onClose }: { roomId: string; linked: ReadonlySet<string>; onClose: () => void }) {
  const t = useT();
  const token = useApp((s) => s.token);
  const link = async (files: DeskFile[]) => {
    if (!token) return;
    try {
      await api.setFileRooms(token, files.map((file) => file.id), [roomId], []);
      toast(t('cloud.share.saved'), 'success');
      onClose();
    } catch (error) {
      toast(describeError(error), 'error');
    }
  };
  return (
    <CloudFilePicker
      title={t('cloud.panel.pickTitle')}
      action={t('cloud.panel.pickAction')}
      empty={t('cloud.panel.pickEmpty')}
      hide={(file) => linked.has(file.id) || file.rooms.includes(roomId)}
      onPick={link}
      onClose={onClose}
    />
  );
}
